/* eslint-disable @typescript-eslint/no-explicit-any */
import { _getCacheKey, DEFAULT_TTL } from './cache'
import { _buildQueryString, _buildUrlWithUrlParams } from './tools'
import { _FetchConfigData, _FetchOptions, _RequestDefinition } from './types'

export const _doFetch =
  (fetchConfigData: _FetchConfigData) =>
  async <Def extends _RequestDefinition<any, any, any>>(
    def: Def,
    options?: _FetchOptions<Def['urlParams'], Def['fetchParams']> & {
      signal?: AbortSignal
    }
  ): Promise<Def['response']> => {
    const { cache, inflight, requestMiddlewares, errorMiddlewares } =
      fetchConfigData
    const {
      urlParams,
      fetchParams,
      useCache = def.method === 'GET',
      retry = 0,
      retryDelay = 0,
      ttl = DEFAULT_TTL,
      signal
    } = {
      ...def,
      ...options
    }
    const url = _buildUrlWithUrlParams(def.url, urlParams as any)

    const init: RequestInit & { url: string; baseUrl?: string } = {
      method: def.method,
      headers: { 'Content-Type': 'application/json' },
      url,
      signal
    }

    if (def.method !== 'GET' && fetchParams) {
      init.body = JSON.stringify(fetchParams)
    } else if (def.method === 'GET' && fetchParams) {
      init.url = url + _buildQueryString(fetchParams as any)
    }

    let config = init
    for (const mw of requestMiddlewares) {
      config = await mw(config, { urlParams, fetchParams, def })
    }

    const cacheKey = _getCacheKey({
      url: def.url,
      method: def.method,
      fetchParams,
      urlParams
    })

    if (useCache) {
      if (cache.has(cacheKey)) {
        const cachedItem = cache.get(cacheKey)
        if (cachedItem.expireAt > Date.now()) {
          return cachedItem.data
        }
        cache.delete(cacheKey)
      }
      if (inflight.has(cacheKey)) return inflight.get(cacheKey)
    }

    const doRequest = async (retryLeft: number): Promise<any> => {
      try {
        const res = await fetch(`${config.baseUrl ?? ''}${config.url}`, config)
        if (!res.ok) {
          const errorText = await res.text()
          let errorData
          try {
            errorData = JSON.parse(errorText)
          } catch (e) {
            errorData = errorText
          }

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const error: any = new Error(res.statusText || 'Request failed')
          error.response = {
            data: errorData,
            status: res.status,
            statusText: res.statusText
          }
          throw error
        }
        const text = await res.text()
        const contentType = res.headers.get('content-type') || ''
        const data = contentType.includes('application/json')
          ? JSON.parse(text)
          : text

        if (useCache) {
          cache.set(cacheKey, {
            data,
            expireAt: Date.now() + ttl
          })
        }

        return data
      } catch (err) {
        if (retryLeft > 0) {
          if (retryDelay) await new Promise((r) => setTimeout(r, retryDelay))
          return doRequest(retryLeft - 1)
        }
        for (const emw of errorMiddlewares) {
          err = await emw(err, config)
        }

        console.error('error', err);

        return
      }
    }

    const req = doRequest(retry)
    if (useCache) inflight.set(cacheKey, req)
    const result = await req
    inflight.delete(cacheKey)
    return result
  }
